// 👥 Employés E5 — Assistant de regroupement de fiches en double (propriétaire) : 3 étapes, jamais de suppression.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CLES = [['numero_employe', 'Numéro'], ['email', 'Courriel'], ['telephone', 'Téléphone'], ['type_employe', 'Type'], ['date_embauche', 'Embauche'], ['roles', 'Rôles'], ['nb_champs', 'Champs remplis'], ['date_creation', 'Créée le']];
const txt = (v) => (Array.isArray(v) ? v.join(', ') : v === true ? 'oui' : v === false ? 'non' : v == null || v === '' ? '—' : String(v).slice(0, 40));

function CarteFiche({ fiche, choisie, defaut, onChoisir }) {
  return (
    <button type="button" onClick={onChoisir} data-testid={`fusion-choix-${fiche.id}`}
      className={`text-left border rounded-lg p-3 flex-1 min-w-[220px] ${choisie ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="font-semibold">{fiche.nom}</span>
        <Badge className={fiche.est_actif ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>{fiche.est_actif ? 'active' : 'inactive'}</Badge>
        {defaut && <Badge variant="outline" data-testid={`fusion-defaut-${fiche.id}`}>par défaut</Badge>}
        {choisie && <Badge className="bg-blue-600 text-white">conservée</Badge>}
      </div>
      <dl className="text-xs grid grid-cols-[auto,1fr] gap-x-2 gap-y-0.5">{CLES.map(([k, l]) => <div key={k} className="contents"><dt className="text-slate-500">{l}</dt><dd className="text-slate-800">{txt(fiche[k])}</dd></div>)}</dl>
    </button>
  );
}

export function AssistantFusionFiches({ ids, ouvert, onFermer, onTermine }) {
  const [etape, setEtape] = useState(1);
  const [prop, setProp] = useState(null);
  const [conservee, setConservee] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [confirme, setConfirme] = useState(false);
  const [resultat, setResultat] = useState(null);
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(false);

  useEffect(() => {
    if (!ouvert || !ids?.length) return;
    setEtape(1); setApercu(null); setConfirme(false); setResultat(null); setErreur('');
    axios.get(`${API_URL}/api/employes/fusion/proposition`, { params: { ids: ids.join(',') } })
      .then((r) => { setProp(r.data); setConservee(r.data.conservee_id); })
      .catch((e) => setErreur(e.response?.data?.detail || 'Proposition impossible'));
  }, [ouvert, ids]);

  const autreId = prop ? prop.fiches.map((f) => f.id).filter((i) => i !== conservee)[0] : null;

  const voirApercu = () => {
    setChargement(true); setErreur('');
    axios.post(`${API_URL}/api/employes/fusion/apercu`, { conservee_id: conservee, autre_id: autreId })
      .then((r) => { setApercu(r.data); setEtape(2); })
      .catch((e) => setErreur(String(e.response?.data?.detail?.message || e.response?.data?.detail || 'Aperçu impossible')))
      .finally(() => setChargement(false));
  };

  const executer = () => {
    setChargement(true); setErreur('');
    axios.post(`${API_URL}/api/employes/fusion/executer`, { conservee_id: conservee, autre_id: autreId, confirmation: true })
      .then((r) => { setResultat(r.data); setEtape(4); toast.success('Fiches regroupées'); onTermine?.(r.data); })
      .catch((e) => setErreur(String(e.response?.data?.detail?.message || e.response?.data?.detail || 'Regroupement impossible')))
      .finally(() => setChargement(false));
  };

  const bloque = (apercu?.conflits_bloquants || []).length > 0;
  const nomC = prop?.fiches.find((f) => f.id === conservee)?.nom;
  const nomA = prop?.fiches.find((f) => f.id === autreId)?.nom;

  return (
    <Dialog open={ouvert} onOpenChange={(o) => { if (!o) onFermer(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="assistant-fusion">
        <DialogHeader>
          <DialogTitle>Regrouper des fiches en double — étape {Math.min(etape, 3)} / 3</DialogTitle>
          <DialogDescription>Aucune donnée n'est supprimée : l'autre fiche est archivée et toutes ses données sont reliées à la fiche conservée. Un journal complet est gardé.</DialogDescription>
        </DialogHeader>
        {erreur && <p className="text-sm text-red-600" data-testid="fusion-erreur">{erreur}</p>}

        {etape === 1 && prop && (
          <div className="space-y-3" data-testid="fusion-etape-1">
            <p className="text-sm">Fiche conservée proposée : <strong>{prop.fiches.find((f) => f.id === prop.conservee_id)?.nom}</strong> <span className="text-slate-500">({prop.raison})</span>. Vous pouvez choisir l'autre.</p>
            {prop.note && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="fusion-note-groupe">{prop.note}</p>}
            <div className="flex flex-wrap gap-3">{prop.fiches.slice(0, 2).map((f) => <CarteFiche key={f.id} fiche={f} choisie={f.id === conservee} defaut={f.id === prop.conservee_id} onChoisir={() => setConservee(f.id)} />)}</div>
          </div>
        )}

        {etape === 2 && apercu && (
          <div className="space-y-3 text-sm" data-testid="fusion-etape-2">
            <p><strong>{apercu.conservee.nom}</strong> conservée · <strong>{apercu.autre.nom}</strong> archivée · {apercu.total_references} référence(s) à relier.</p>
            {bloque && <div className="border border-red-300 bg-red-50 rounded p-3 text-red-800" data-testid="fusion-conflit-bloquant"><p className="font-semibold">Conflit bloquant</p>{apercu.conflits_bloquants.map((c, i) => <p key={i}>{c}</p>)}</div>}
            {apercu.avertissements.length > 0 && <ul className="list-disc pl-5 text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="fusion-avertissements">{apercu.avertissements.map((a, i) => <li key={i}>{a}</li>)}</ul>}
            <table className="w-full text-xs border" data-testid="fusion-impacts"><thead><tr className="bg-slate-50"><th className="text-left p-1">Données reliées</th><th className="text-right p-1">Nombre</th></tr></thead>
              <tbody>{apercu.impacts.map((it) => <tr key={`${it.collection}.${it.champ}`} className="border-t" data-testid={`fusion-impact-${it.collection}-${it.champ}`}><td className="p-1">{it.libelle}</td><td className="p-1 text-right">{it.nb}</td></tr>)}
                {apercu.impacts.length === 0 && <tr><td className="p-1 text-slate-500" colSpan={2}>Aucune donnée liée à la fiche archivée.</td></tr>}</tbody></table>
            <div className="grid sm:grid-cols-2 gap-3">
              <div data-testid="fusion-champs-completes"><p className="font-medium">Champs complétés sur la fiche conservée</p>{Object.keys(apercu.champs_completes).length ? <ul className="list-disc pl-5">{Object.entries(apercu.champs_completes).map(([k, v]) => <li key={k}>{k} : {txt(v)}</li>)}</ul> : <p className="text-slate-500">aucun</p>}</div>
              <div><p className="font-medium">Rôles résultants</p><p data-testid="fusion-roles">{apercu.roles_resultants.join(', ')} <span className="text-slate-500">(principal : {apercu.role_principal || '—'})</span></p>
                <p className="font-medium mt-2">Comptes</p>
                <p data-testid="fusion-comptes">{apercu.compte_conserve ? <>conservé : {apercu.compte_conserve.email}</> : 'aucun compte'}{apercu.compte_fusionne && <> · fusionné (désactivé) : {apercu.compte_fusionne.email}</>} <span className="text-slate-500">— {apercu.raison_compte}</span></p></div>
            </div>
            <p className="text-xs text-slate-500">{apercu.non_reecrit}</p>
          </div>
        )}

        {etape === 3 && (
          <div className="space-y-3 text-sm" data-testid="fusion-etape-3">
            <p>Vous allez regrouper <strong>{nomA}</strong> dans <strong>{nomC}</strong>.</p>
            <label className="flex items-start gap-2 cursor-pointer"><Checkbox checked={confirme} onCheckedChange={(v) => setConfirme(v === true)} data-testid="fusion-confirmation-case" /><span>Je comprends que l'autre fiche sera archivée et que toutes ses données seront reliées à la fiche conservée.</span></label>
          </div>
        )}

        {etape === 4 && resultat && (
          <div className="space-y-2 text-sm" data-testid="fusion-resume">
            <p className="font-semibold text-emerald-700">Regroupement terminé.</p>
            <p>{resultat.operations.filter((o) => o.statut === 'terminee').length} opération(s) journalisée(s) · journal {resultat.id.slice(0, 8)}…</p>
            {(resultat.references_residuelles || []).length > 0 && <p className="text-amber-800" data-testid="fusion-residuelles">Références résiduelles à vérifier : {resultat.references_residuelles.map((r) => `${r.collection} (${r.nb})`).join(', ')}</p>}
          </div>
        )}

        <DialogFooter className="gap-2">
          {etape > 1 && etape < 4 && <Button variant="outline" onClick={() => setEtape(etape - 1)} data-testid="fusion-precedent">Précédent</Button>}
          {etape === 1 && <Button onClick={voirApercu} disabled={!conservee || chargement} data-testid="fusion-suivant-1">Voir les impacts</Button>}
          {etape === 2 && <Button onClick={() => setEtape(3)} disabled={bloque || chargement} data-testid="fusion-suivant-2">Continuer</Button>}
          {etape === 3 && <Button onClick={executer} disabled={!confirme || chargement} className="bg-red-600 hover:bg-red-700" data-testid="fusion-executer">Regrouper maintenant</Button>}
          {etape === 4 && <Button onClick={onFermer} data-testid="fusion-fermer">Fermer</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
